import React, { useState, useEffect } from 'react';
import { X, FileText, MessageCircle, BookOpen } from 'react-feather';
import ChatMarkdown from './ChatMarkdown';
import { base64ToFile } from '../utils/fileStorage';
import styles from '../styles/focusView.module.css';

const FocusView = ({ 
  document: docData, 
  content, 
  contentType = 'notes', // 'notes', 'quotes', 'chat'
  title = 'Study Notes',
  onClose 
}) => {
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState('content'); // 'content' or 'chat'
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState(null);

  // Create blob URL for document preview
  useEffect(() => {
    if (docData?.content) {
      try {
        const file = base64ToFile(docData.content, docData.name, docData.type);
        const url = URL.createObjectURL(file);
        setDocumentUrl(url);
        return () => URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error creating document URL:', error);
      }
    }
  }, [docData]);

  // Handle resize drag
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const container = document.querySelector(`.${styles.focusViewContainer}`);
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPanelWidth(Math.min(Math.max(newWidth, 20), 80));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle chat with document context
  const handleSendChat = async () => {
    if (!chatInput.trim() || isLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          context: `Document: ${docData?.name}\n\nDocument Content:\n${docData?.content?.substring(0, 10000) || ''}\n\nGenerated Notes:\n${content?.substring(0, 5000) || ''}`,
          history: chatMessages.map(m => ({ role: m.role, content: m.content })),
          stream: false
        })
      });

      const data = await response.json();
      if (data.response) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDocumentPreview = () => {
    if (!docData) {
      return (
        <div className={styles.emptyState}>
          <FileText size={48} />
          <p>No document selected</p>
        </div>
      );
    }

    const isPdf = docData.type === 'application/pdf' || docData.name?.toLowerCase().endsWith('.pdf');

    if (isPdf && documentUrl) {
      return (
        <iframe
          src={documentUrl}
          className={styles.pdfViewer}
          title={docData.name}
        />
      );
    }

    // For text-based documents, show the content
    return (
      <div className={styles.textViewer}>
        <pre>{docData.content}</pre>
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'chat') {
      return (
        <div className={styles.chatContainer}>
          <div className={styles.chatMessages}>
            {chatMessages.length === 0 ? (
              <div className={styles.chatEmpty}>
                <MessageCircle size={32} />
                <p>Ask questions about your document and notes</p>
                <div className={styles.chatSuggestions}>
                  <button onClick={() => setChatInput('Summarize the key points')}>
                    Summarize key points
                  </button>
                  <button onClick={() => setChatInput('What are the main themes?')}>
                    Main themes
                  </button>
                  <button onClick={() => setChatInput('Explain this in simpler terms')}>
                    Simplify
                  </button>
                </div>
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div key={idx} className={`${styles.chatMessage} ${styles[msg.role]}`}>
                  <div className={styles.messageContent}>
                    <ChatMarkdown content={msg.content} />
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className={`${styles.chatMessage} ${styles.assistant}`}>
                <div className={styles.messageContent}>
                  <div className={styles.typingIndicator}>
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.chatInputContainer}>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Ask about your document..."
              className={styles.chatInput}
            />
            <button 
              onClick={handleSendChat} 
              disabled={isLoading || !chatInput.trim()}
              className={styles.sendButton}
            >
              Send
            </button>
          </div>
        </div>
      );
    }

    // Notes/Quotes content
    return (
      <div className={styles.notesContainer}>
        <div className={styles.notesContent}>
          <ChatMarkdown content={content} />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.focusViewOverlay}>
      <div className={styles.focusViewContainer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <BookOpen size={20} />
            <span className={styles.headerTitle}>{title}</span>
            {docData && (
              <span className={styles.documentName}>• {docData.name}</span>
            )}
          </div>
          <div className={styles.headerRight}>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Left Panel - Document */}
          <div 
            className={styles.leftPanel}
            style={{ width: `${leftPanelWidth}%` }}
          >
            <div className={styles.panelHeader}>
              <FileText size={16} />
              <span>Document</span>
            </div>
            <div className={styles.panelContent}>
              {renderDocumentPreview()}
            </div>
          </div>

          {/* Resize Handle */}
          <div 
            className={`${styles.resizeHandle} ${isDragging ? styles.dragging : ''}`}
            onMouseDown={handleMouseDown}
          >
            <div className={styles.resizeBar} />
          </div>

          {/* Right Panel - Notes/Chat */}
          <div 
            className={styles.rightPanel}
            style={{ width: `${100 - leftPanelWidth}%` }}
          >
            <div className={styles.panelHeader}>
              <div className={styles.tabButtons}>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'content' ? styles.active : ''}`}
                  onClick={() => setActiveTab('content')}
                >
                  <BookOpen size={14} />
                  {contentType === 'notes' ? 'Notes' : contentType === 'quotes' ? 'Quotes' : 'Content'}
                </button>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'chat' ? styles.active : ''}`}
                  onClick={() => setActiveTab('chat')}
                >
                  <MessageCircle size={14} />
                  Chat
                </button>
              </div>
            </div>
            <div className={styles.panelContent}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FocusView;
